import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  TruckIcon,
  CubeIcon,
  ArrowUturnLeftIcon,
  PrinterIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  ArchiveBoxXMarkIcon,
  XMarkIcon,
  QrCodeIcon,
  ArrowsRightLeftIcon,
} from "@heroicons/react/24/outline";
import QRCode from "react-qr-code";
import moment from "moment";
import { request } from "config/request";
import UseToast from "utils/AfToast";
import { soundFX } from "./soundEffects";
import { AwbPackingTab } from "./components/AwbPackingTab";
import { BoxExplorerTab } from "./components/BoxExplorerTab";
import { ReturnCheckInTab } from "./components/ReturnCheckInTab";
import { StickerPrinterTab } from "./components/StickerPrinterTab";
import { TransfersTab } from "./components/TransfersTab";

// ── Shared Badges ──
export const BoxStatusBadge: React.FC<{ status: string; size?: "sm" | "md" }> = ({
  status,
  size = "md",
}) => {
  const norm = (status || "").toUpperCase();
  let bg = "bg-slate-100 text-slate-700 border-slate-200";
  let label = status || "UNKNOWN";
  let Icon: any = CubeIcon;

  switch (norm) {
    case "IN_STOCK":
      bg = "bg-emerald-50 text-emerald-700 border-emerald-300";
      label = "In Stock";
      Icon = CheckCircleIcon;
      break;
    case "ASSIGNED":
      bg = "bg-sky-50 text-sky-700 border-sky-300";
      label = "Assigned";
      Icon = ClockIcon;
      break;
    case "IN_TRANSIT":
      bg = "bg-indigo-50 text-indigo-700 border-indigo-300";
      label = "In Transit";
      Icon = TruckIcon;
      break;
    case "IN_TRANSFER":
      bg = "bg-purple-50 text-purple-700 border-purple-300";
      label = "In Transfer";
      Icon = ArrowsRightLeftIcon;
      break;
    case "DELIVERED":
      bg = "bg-teal-50 text-teal-700 border-teal-300";
      label = "Delivered";
      Icon = CheckCircleIcon;
      break;
    case "AWAITING_RETURN":
      bg = "bg-amber-50 text-amber-700 border-amber-300";
      label = "Awaiting Return";
      Icon = ArrowUturnLeftIcon;
      break;
    case "DISPOSED":
      bg = "bg-gray-100 text-gray-500 border-gray-300";
      label = "Disposed";
      Icon = ArchiveBoxXMarkIcon;
      break;
    case "DAMAGED":
      bg = "bg-rose-50 text-rose-700 border-rose-300 font-semibold";
      label = "Damaged";
      Icon = ExclamationTriangleIcon;
      break;
  }

  const px = size === "sm" ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-xs";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${px} ${bg}`}>
      <Icon className={size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} />
      <span>{label}</span>
    </span>
  );
};

export const BoxTypeBadge: React.FC<{ type: string; usageCount?: number; size?: "sm" | "md" }> = ({
  type,
  usageCount,
  size = "md",
}) => {
  const isMulti = type === "MULTI_USE";
  const px = size === "sm" ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-xs";

  if (isMulti) {
    return (
      <span className={`inline-flex items-center gap-1 rounded-full border border-teal-300 bg-teal-50 text-teal-800 font-medium ${px}`}>
        <ArrowUturnLeftIcon className={size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} />
        <span>Multi-Use</span>
        {typeof usageCount === "number" && (
          <span className="ml-0.5 bg-teal-200 text-teal-900 rounded-full px-1.5 py-0 text-[10px] font-bold">
            Trip #{usageCount}
          </span>
        )}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 text-slate-700 font-medium ${px}`}>
      <CubeIcon className={size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} />
      <span>Single-Use</span>
    </span>
  );
};

// ── Modal: Replace Damaged Box ──
export const ReplaceDamagedModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  defaultDamagedQr?: string;
  defaultAwbNo?: string;
  onSuccess?: (res: any) => void;
}> = ({ isOpen, onClose, defaultDamagedQr = "", defaultAwbNo = "", onSuccess }) => {
  const [damagedQr, setDamagedQr] = useState(defaultDamagedQr);
  const [newQr, setNewQr] = useState("");
  const [awbNo, setAwbNo] = useState(defaultAwbNo);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (isOpen) {
      setDamagedQr(defaultDamagedQr);
      setAwbNo(defaultAwbNo);
      setNewQr("");
      setReason("");
      setErrorMsg("");
    }
  }, [isOpen, defaultDamagedQr, defaultAwbNo]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");

    if (!damagedQr.trim() || !newQr.trim() || !reason.trim()) {
      setErrorMsg("Please fill in damaged QR, replacement QR, and damage reason.");
      soundFX.playError();
      return;
    }
    if (damagedQr.trim().toUpperCase() === newQr.trim().toUpperCase()) {
      setErrorMsg("Damaged box and replacement box cannot have the same QR code!");
      soundFX.playError();
      return;
    }

    try {
      setLoading(true);
      const res: any = await request({
        url: "/admin/box-tracker/replace-damaged",
        method: "POST",
        body: {
          damagedQrCode: damagedQr.trim().toUpperCase(),
          newQrCode: newQr.trim().toUpperCase(),
          awbNo: awbNo.trim() || undefined,
          reason: reason.trim(),
        },
      });

      if (res?.status === "SUCCESS") {
        soundFX.playSuccess();
        UseToast(res.message || "Box replaced successfully!", "success");
        if (onSuccess) onSuccess(res.data);
        onClose();
      } else {
        soundFX.playError();
        setErrorMsg(res?.message || "Failed to replace damaged box.");
      }
    } catch (err: any) {
      soundFX.playError();
      setErrorMsg(typeof err === "string" ? err : err?.message || "Error replacing box.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-5 py-3.5 bg-rose-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-white" />
            <h2 className="text-sm font-bold">Replace Damaged Box</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded text-white/80 hover:text-white">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 overflow-y-auto space-y-3.5">
          {errorMsg && (
            <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-rose-800 text-xs font-medium">
              {errorMsg}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-1 gap-3 p-3 bg-slate-50 border rounded-lg">
            <div>
              <label className="block text-xs font-semibold text-rose-900 mb-1">Damaged Box QR *</label>
              <input
                type="text"
                value={damagedQr}
                onChange={(e) => setDamagedQr(e.target.value.toUpperCase())}
                placeholder="e.g. 123123, BX-123123, AHD123123"
                className="w-full px-2.5 py-1.5 text-xs border border-rose-300 rounded font-mono font-bold bg-white text-slate-800 uppercase"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-emerald-900 mb-1">New Box QR *</label>
              <input
                type="text"
                value={newQr}
                onChange={(e) => setNewQr(e.target.value.toUpperCase())}
                placeholder="e.g. 123123, BX-123123, AHD123123"
                className="w-full px-2.5 py-1.5 text-xs border border-emerald-300 rounded font-mono font-bold bg-white text-slate-800 uppercase"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">AWB Number (optional)</label>
            <input
              type="text"
              value={awbNo}
              onChange={(e) => setAwbNo(e.target.value)}
              placeholder="e.g. A12345678"
              className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Damage Reason *</label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe damage (e.g. Crushed, water leakage)..."
              className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded text-slate-800"
              required
            />
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {["Crushed during loading", "Water damage", "Torn seams", "QR unreadable"].map((r, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => setReason(r)}
                  className={`text-[11px] px-2 py-0.5 rounded border ${
                    reason === r ? "bg-rose-100 text-rose-800 border-rose-300 font-bold" : "bg-slate-100 text-slate-600 border-slate-200"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose} disabled={loading} className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="px-4 py-1.5 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded disabled:opacity-50">
              {loading ? "Processing..." : "Confirm Replacement"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Drawer: Box Journey Timeline ──
export const BoxTimelineDrawer: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  qrCode: string | null;
  onReplaceDamaged?: (boxQr: string, awbNo?: string) => void;
}> = ({ isOpen, onClose, qrCode, onReplaceDamaged }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [showQrPreview, setShowQrPreview] = useState(false);

  useEffect(() => {
    if (isOpen && qrCode) {
      setLoading(true);
      setErrorMsg("");
      request({ url: `/admin/box-tracker/by-qr/${encodeURIComponent(qrCode.trim())}`, method: "GET" })
        .then((res: any) => {
          if (res?.status === "SUCCESS") setData(res.data);
          else setErrorMsg(res?.message || "No records found.");
        })
        .catch((err: any) => setErrorMsg(typeof err === "string" ? err : err?.message || "Failed to load timeline."))
        .finally(() => setLoading(false));
    } else {
      setData(null);
      setErrorMsg("");
      setShowQrPreview(false);
    }
  }, [isOpen, qrCode]);

  if (!isOpen) return null;

  const box = data?.box;
  const historyLogs = data?.historyLogs || [];

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden bg-black/40 backdrop-blur-sm animate-fadeIn">
      <div className="absolute inset-y-0 right-0 max-w-full flex pl-10">
        <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col">
          <div className="p-4 bg-slate-900 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CubeIcon className="w-5 h-5 text-sky-400" />
              <h3 className="text-sm font-bold font-mono">{qrCode}</h3>
              <button type="button" onClick={() => setShowQrPreview(!showQrPreview)} className="p-1 rounded bg-white/20 text-xs" title="Toggle QR">
                <QrCodeIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            <button type="button" onClick={onClose} className="p-1 rounded text-white/80 hover:text-white">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {showQrPreview && qrCode && (
            <div className="p-3 bg-sky-50 border-b flex flex-col items-center justify-center">
              <div className="p-2 bg-white rounded shadow-sm border">
                <QRCode value={qrCode} size={90} />
              </div>
              <p className="text-xs font-mono font-bold text-slate-700 mt-1">{qrCode}</p>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {loading ? (
              <div className="py-16 text-center text-slate-400 text-xs">Loading timeline...</div>
            ) : errorMsg ? (
              <div className="p-3 bg-rose-50 border rounded text-rose-800 text-xs">{errorMsg}</div>
            ) : box ? (
              <>
                <div className="p-3 rounded-lg bg-slate-50 border space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <BoxStatusBadge status={box.status} size="sm" />
                    <BoxTypeBadge type={box.boxType} usageCount={box.usageCount} size="sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 pt-1 border-t text-[11px]">
                    <div><span className="text-slate-400">Current AWB:</span> <span className="font-mono font-bold">{box.currentAwbNo || "—"}</span></div>
                    <div><span className="text-slate-400">Hub:</span> <span>{box._subbranch?.branch_name || "—"}</span></div>
                    <div><span className="text-slate-400">Trips:</span> <span className="font-bold">{box.usageCount ?? 0}</span></div>
                    <div><span className="text-slate-400">Status:</span> <span>{box.status}</span></div>
                  </div>
                  {box.damageReason && (
                    <div className="p-2 rounded bg-rose-50 text-rose-800 text-[11px]">
                      Damage: {box.damageReason}
                    </div>
                  )}
                  {box.status !== "DAMAGED" && box.status !== "DISPOSED" && onReplaceDamaged && (
                    <button
                      type="button"
                      onClick={() => onReplaceDamaged(box.qrCode, box.currentAwbNo || undefined)}
                      className="w-full py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 border rounded text-xs font-semibold text-center mt-1"
                    >
                      Replace Damaged
                    </button>
                  )}
                </div>

                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                    History Logs ({historyLogs.length})
                  </h4>
                  {historyLogs.length === 0 ? (
                    <div className="text-center py-6 text-slate-400 text-xs border border-dashed rounded">No logs.</div>
                  ) : (
                    <div className="space-y-2">
                      {historyLogs.map((log: any, idx: number) => (
                        <div key={log.id || idx} className="p-2.5 rounded border bg-slate-50 text-xs space-y-0.5">
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="font-bold text-slate-800">{log.action}</span>
                            <span className="text-slate-400 text-[10px]">
                              {log.createdAt ? (moment(log.createdAt, ["DD-MM-YYYY HH:mm:ss", "YYYY-MM-DD HH:mm:ss", moment.ISO_8601]).isValid() ? moment(log.createdAt, ["DD-MM-YYYY HH:mm:ss", "YYYY-MM-DD HH:mm:ss", moment.ISO_8601]).format("DD MMM, hh:mm A") : log.createdAt) : "—"}
                            </span>
                          </div>
                          <p className="text-slate-600 text-[11px]">{log.remarks}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>

          <div className="p-3 bg-slate-50 border-t flex justify-end">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border rounded">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Drawer: AWB Consignment Boxes Lookup ──
export const AwbLookupDrawer: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  awbNo?: string;
  onSelectBoxTimeline?: (qrCode: string) => void;
  onReplaceDamaged?: (damagedQr: string, awbNo: string) => void;
}> = ({ isOpen, onClose, awbNo: initialAwb = "", onSelectBoxTimeline, onReplaceDamaged }) => {
  const [awbQuery, setAwbQuery] = useState(initialAwb);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchAwbData = (awb: string) => {
    if (!awb.trim()) return;
    setLoading(true);
    setErrorMsg("");
    request({ url: `/admin/box-tracker/by-awb/${encodeURIComponent(awb.trim())}`, method: "GET" })
      .then((res: any) => {
        if (res?.status === "SUCCESS") setData(res.data);
        else setErrorMsg(res?.message || `No boxes linked to AWB #${awb}`);
      })
      .catch((err: any) => setErrorMsg(typeof err === "string" ? err : err?.message || "Failed to load AWB."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isOpen) {
      setAwbQuery(initialAwb);
      if (initialAwb.trim()) fetchAwbData(initialAwb.trim());
      else { setData(null); setErrorMsg(""); }
    }
  }, [isOpen, initialAwb]);

  if (!isOpen) return null;

  const activeBoxes = data?.activeBoxes || [];
  const replacedBoxes = data?.replacedBoxes || [];

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden bg-black/40 backdrop-blur-sm animate-fadeIn">
      <div className="absolute inset-y-0 right-0 max-w-full flex pl-10">
        <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col">
          <div className="p-4 bg-slate-900 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TruckIcon className="w-5 h-5 text-sky-400" />
              <h3 className="text-sm font-bold">AWB Consignment Boxes</h3>
            </div>
            <button type="button" onClick={onClose} className="p-1 rounded text-white/80 hover:text-white">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="p-3 bg-slate-50 border-b">
            <form onSubmit={(e) => { e.preventDefault(); fetchAwbData(awbQuery); }} className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={awbQuery}
                  onChange={(e) => setAwbQuery(e.target.value)}
                  placeholder="Enter AWB Number (e.g. A12345678)..."
                  className="w-full pl-8 pr-2 py-1.5 text-xs border rounded font-mono"
                />
                <MagnifyingGlassIcon className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2" />
              </div>
              <button type="submit" disabled={loading} className="px-3 py-1.5 bg-[#0077B6] hover:bg-[#023e8a] text-white text-xs font-semibold rounded">
                Search
              </button>
            </form>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {loading ? (
              <div className="py-16 text-center text-slate-400 text-xs">Loading AWB boxes...</div>
            ) : errorMsg ? (
              <div className="p-3 bg-rose-50 border rounded text-rose-800 text-xs">{errorMsg}</div>
            ) : data ? (
              <>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                    Active Boxes ({activeBoxes.length})
                  </h4>
                  {activeBoxes.length === 0 ? (
                    <div className="p-3 rounded border border-dashed text-slate-400 text-xs text-center">No active boxes.</div>
                  ) : (
                    <div className="space-y-2">
                      {activeBoxes.map((b: any) => (
                        <div key={b.id || b.qrCode} className="p-2.5 rounded border bg-white space-y-1 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="font-mono font-bold">{b.qrCode}</span>
                            <BoxStatusBadge status={b.status} size="sm" />
                          </div>
                          <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t">
                            <BoxTypeBadge type={b.boxType} usageCount={b.usageCount} size="sm" />
                            <div className="flex items-center gap-2">
                              {onSelectBoxTimeline && (
                                <button type="button" onClick={() => onSelectBoxTimeline(b.qrCode)} className="text-[#0077B6] font-semibold hover:underline">
                                  Journey &rarr;
                                </button>
                              )}
                              {onReplaceDamaged && b.status !== "DAMAGED" && (
                                <button type="button" onClick={() => onReplaceDamaged(b.qrCode, data.awbNo)} className="text-rose-600 font-semibold hover:underline">
                                  Replace
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {replacedBoxes.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-rose-700 mb-2">
                      Replaced / Damaged ({replacedBoxes.length})
                    </h4>
                    <div className="space-y-2">
                      {replacedBoxes.map((rb: any) => (
                        <div key={rb.id || rb.qrCode} className="p-2.5 rounded border border-rose-200 bg-rose-50/40 text-xs space-y-0.5">
                          <div className="flex items-center justify-between">
                            <span className="font-mono font-bold text-rose-900">{rb.qrCode}</span>
                            <BoxStatusBadge status={rb.status || "DAMAGED"} size="sm" />
                          </div>
                          {rb.damageReason && <p className="text-rose-800 text-[11px]">Reason: {rb.damageReason}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16 text-slate-400 text-xs">Enter an AWB to search.</div>
            )}
          </div>

          <div className="p-3 bg-slate-50 border-t flex justify-end">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border rounded">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Page Component ──
const BoxTracker: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab") as "packing" | "explorer" | "returns" | "transfers" | "stickers" | null;
  const [activeTab, setActiveTab] = useState<"packing" | "explorer" | "returns" | "transfers" | "stickers">(
    tabParam && ["packing", "explorer", "returns", "transfers", "stickers"].includes(tabParam) ? tabParam : "packing"
  );

  const [soundEnabled, setSoundEnabled] = useState(true);

  // Drawers & Modals
  const [timelineQr, setTimelineQr] = useState<string | null>(null);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);

  const [lookupAwb, setLookupAwb] = useState<string>("");
  const [isAwbLookupOpen, setIsAwbLookupOpen] = useState(false);

  const [replaceModalOpen, setReplaceModalOpen] = useState(false);
  const [replaceDamagedQr, setReplaceDamagedQr] = useState("");
  const [replaceAwbNo, setReplaceAwbNo] = useState("");

  useEffect(() => {
    if (tabParam && ["packing", "explorer", "returns", "transfers", "stickers"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
    const awbFromUrl = searchParams.get("awb");
    if (awbFromUrl) setLookupAwb(awbFromUrl);
    const qrFromUrl = searchParams.get("qr");
    if (qrFromUrl) {
      setTimelineQr(qrFromUrl);
      setIsTimelineOpen(true);
    }
  }, [searchParams]);

  const handleTabChange = (tab: "packing" | "explorer" | "returns" | "transfers" | "stickers") => {
    setActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", tab);
    setSearchParams(newParams);
  };

  const handleToggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    soundFX.enabled = next;
    if (next) soundFX.playSuccess();
  };

  return (
    <div className="min-h-screen bg-slate-50/70 p-6 md:p-4 sm:p-3 space-y-4">
      {/* Clean, Simple Top Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-row sm:flex-col items-center sm:items-start justify-between gap-3 shadow-xs">
        <div className="flex items-center gap-2.5">
          <CubeIcon className="w-6 h-6 text-[#0077B6]" />
          <h1 className="text-lg font-bold text-slate-800">Box Tracking</h1>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleToggleSound}
            className={`p-1.5 rounded-lg border text-xs font-medium flex items-center gap-1.5 transition-all ${
              soundEnabled
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-slate-100 text-slate-500 border-slate-200"
            }`}
            title={soundEnabled ? "Audio: ON" : "Audio: MUTED"}
          >
            {soundEnabled ? (
              <SpeakerWaveIcon className="w-4 h-4 text-emerald-600" />
            ) : (
              <SpeakerXMarkIcon className="w-4 h-4 text-slate-400" />
            )}
            <span className="text-[11px]">{soundEnabled ? "Sound On" : "Muted"}</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setLookupAwb("");
              setIsAwbLookupOpen(true);
            }}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-lg border border-slate-200 flex items-center gap-1"
          >
            <MagnifyingGlassIcon className="w-3.5 h-3.5" />
            <span>AWB Lookup</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setReplaceDamagedQr("");
              setReplaceAwbNo("");
              setReplaceModalOpen(true);
            }}
            className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-medium rounded-lg border border-rose-200 flex items-center gap-1"
          >
            <ExclamationTriangleIcon className="w-3.5 h-3.5 text-rose-600" />
            <span>Replace Damaged</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1.5 border-b border-slate-200 pb-2 overflow-x-auto text-xs font-semibold">
        <button
          type="button"
          onClick={() => handleTabChange("packing")}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg transition-all ${
            activeTab === "packing"
              ? "bg-[#0077B6] text-white shadow-xs"
              : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
          }`}
        >
          <TruckIcon className="w-4 h-4" />
          <span>AWB Box Linking</span>
        </button>

        <button
          type="button"
          onClick={() => handleTabChange("explorer")}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg transition-all ${
            activeTab === "explorer"
              ? "bg-[#0077B6] text-white shadow-xs"
              : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
          }`}
        >
          <CubeIcon className="w-4 h-4" />
          <span>Box Explorer</span>
        </button>

        <button
          type="button"
          onClick={() => handleTabChange("returns")}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg transition-all ${
            activeTab === "returns"
              ? "bg-[#0077B6] text-white shadow-xs"
              : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
          }`}
        >
          <ArrowUturnLeftIcon className="w-4 h-4" />
          <span>Return Check-In</span>
        </button>

        <button
          type="button"
          onClick={() => handleTabChange("transfers")}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg transition-all ${
            activeTab === "transfers"
              ? "bg-[#0077B6] text-white shadow-xs"
              : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
          }`}
        >
          <ArrowsRightLeftIcon className="w-4 h-4" />
          <span>Inter-Branch Transfers</span>
        </button>

        <button
          type="button"
          onClick={() => handleTabChange("stickers")}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg transition-all ${
            activeTab === "stickers"
              ? "bg-[#0077B6] text-white shadow-xs"
              : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
          }`}
        >
          <PrinterIcon className="w-4 h-4" />
          <span>Print Stickers</span>
        </button>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === "packing" && <AwbPackingTab />}
        {activeTab === "explorer" && (
          <BoxExplorerTab
            onOpenTimeline={(qr) => {
              setTimelineQr(qr);
              setIsTimelineOpen(true);
            }}
            onOpenAwbLookup={(awb) => {
              setLookupAwb(awb);
              setIsAwbLookupOpen(true);
            }}
            onOpenReplaceModal={(qr, awb) => {
              setReplaceDamagedQr(qr);
              setReplaceAwbNo(awb || "");
              setReplaceModalOpen(true);
            }}
            onPrintSticker={() => handleTabChange("stickers")}
          />
        )}
        {activeTab === "returns" && (
          <ReturnCheckInTab
            onOpenTimeline={(qr) => {
              setTimelineQr(qr);
              setIsTimelineOpen(true);
            }}
          />
        )}
        {activeTab === "transfers" && <TransfersTab />}
        {activeTab === "stickers" && <StickerPrinterTab />}
      </div>

      {/* Drawers & Modals (z-[9999] so never clipped behind top navbar) */}
      <BoxTimelineDrawer
        isOpen={isTimelineOpen}
        onClose={() => setIsTimelineOpen(false)}
        qrCode={timelineQr}
        onReplaceDamaged={(qr, awb) => {
          setReplaceDamagedQr(qr);
          setReplaceAwbNo(awb || "");
          setReplaceModalOpen(true);
        }}
      />

      <AwbLookupDrawer
        isOpen={isAwbLookupOpen}
        onClose={() => setIsAwbLookupOpen(false)}
        awbNo={lookupAwb}
        onSelectBoxTimeline={(qr) => {
          setTimelineQr(qr);
          setIsTimelineOpen(true);
        }}
        onReplaceDamaged={(qr, awb) => {
          setReplaceDamagedQr(qr);
          setReplaceAwbNo(awb);
          setReplaceModalOpen(true);
        }}
      />

      <ReplaceDamagedModal
        isOpen={replaceModalOpen}
        onClose={() => setReplaceModalOpen(false)}
        defaultDamagedQr={replaceDamagedQr}
        defaultAwbNo={replaceAwbNo}
      />
    </div>
  );
};

export default BoxTracker;
